/**
 * Voice AI — Agent Activity Tab
 * Full activity log timeline for a specific agent.
 * Fetches events from agent-timeline.php (reads SQLite events table).
 * Dispatches: (none — read-only display)
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { agentStyles } from '../../styles/agent-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { agentService } from '../../services/agent-service.js';

export class AgentActivityTab extends LitElement {
  static properties = {
    agent: { type: Object },
    _events: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _page: { type: Number, state: true },
    _totalPages: { type: Number, state: true },
    _total: { type: Number, state: true },
  };

  static styles = [designSystem, componentStyles, agentStyles];

  constructor() {
    super();
    this._events = [];
    this._loading = false;
    this._page = 1;
    this._totalPages = 1;
    this._total = 0;
    this._lastAgentId = null;
  }

  updated(changed) {
    if (changed.has('agent') && this.agent?.id !== this._lastAgentId) {
      this._lastAgentId = this.agent?.id;
      this._page = 1;
      this._loadEvents();
    }
  }

  async _loadEvents() {
    if (!this.agent?.id) return;
    this._loading = true;
    try {
      const data = await agentService.loadAgentTimeline(this.agent.id, {
        page: this._page,
        limit: 30,
      });
      if (data.success) {
        this._events = data.events || [];
        this._total = data.pagination?.total || 0;
        this._totalPages = data.pagination?.totalPages || 1;
      }
    } catch (err) {
      console.error('[ActivityTab] Load failed:', err);
      this._events = [];
    } finally {
      this._loading = false;
    }
  }

  _goToPage(page) {
    if (page < 1 || page > this._totalPages || page === this._page) return;
    this._page = page;
    this._loadEvents();
  }

  _formatPhone(phone) {
    if (!phone) return 'unknown';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  }

  _formatDuration(seconds) {
    if (!seconds && seconds !== 0) return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  _eventIcon(type) {
    const icons = {
      call_started: 'fa-phone-arrow-down-left',
      call_ended: 'fa-phone-hangup',
      reservation_created: 'fa-calendar-check',
      payment_link_sent: 'fa-credit-card',
      tool_call_completed: 'fa-wrench',
      tool_call_failed: 'fa-circle-exclamation',
    };
    return icons[type] || 'fa-circle-dot';
  }

  _eventText(evt) {
    const d = evt.data || {};
    switch (evt.eventType) {
      case 'call_started': {
        const dir = d.direction === 'outbound' ? 'Outbound call to' : 'Handled inbound call from';
        return `${dir} ${this._formatPhone(d.phone_number)}`;
      }
      case 'call_ended': {
        const dur = d.duration_seconds ? ` (${this._formatDuration(d.duration_seconds)})` : '';
        const phone = d.phone_number ? ` with ${this._formatPhone(d.phone_number)}` : '';
        return `Completed call${phone}${dur}`;
      }
      case 'reservation_created': {
        const room = d.room_name ? ` for ${d.room_name}` : '';
        const conf = d.confirmation_number ? ` #${d.confirmation_number}` : '';
        return `Created reservation${conf}${room}`;
      }
      case 'payment_link_sent': {
        const order = d.order_id ? ` for order #${d.order_id}` : '';
        return `Sent payment link${order}`;
      }
      case 'tool_call_completed':
        return `Executed ${d.tool_name || 'tool'} successfully`;
      case 'tool_call_failed':
        return `Tool ${d.tool_name || 'unknown'} failed`;
      default:
        return evt.eventType.replace(/_/g, ' ');
    }
  }

  _relativeTime(dateStr) {
    return agentService.relativeTime(dateStr);
  }

  _renderSkeleton() {
    return html`
      <div class="recent-calls-section">
        <div class="section-header">
          <div class="skeleton-line" style="width:160px;height:18px;margin-bottom:0;"></div>
        </div>
        <div style="padding-left:var(--space-6);position:relative;">
          ${[1, 2, 3, 4, 5, 6, 7, 8].map(() => html`
            <div style="padding-bottom:var(--space-4);padding-left:var(--space-4);">
              <div class="skeleton-line" style="width:${55 + Math.random() * 30}%;height:14px;margin-bottom:4px;"></div>
              <div class="skeleton-line" style="width:25%;height:10px;margin-bottom:0;"></div>
            </div>
          `)}
        </div>
      </div>
    `;
  }

  _renderPagination() {
    if (this._total <= 30) return '';

    const startIdx = (this._page - 1) * 30;
    const endIdx = Math.min(startIdx + this._events.length, this._total);

    return html`
      <div class="activity-pagination">
        <div class="activity-pagination-info">
          Showing <strong>${startIdx + 1}-${endIdx}</strong> of <strong>${this._total}</strong>
        </div>
        <div class="activity-pagination-controls">
          <button class="activity-page-btn" ?disabled=${this._page <= 1} @click=${() => this._goToPage(this._page - 1)}>
            <i class="fa-sharp fa-light fa-chevron-left"></i>
          </button>
          ${this._renderPageNumbers()}
          <button class="activity-page-btn" ?disabled=${this._page >= this._totalPages} @click=${() => this._goToPage(this._page + 1)}>
            <i class="fa-sharp fa-light fa-chevron-right"></i>
          </button>
        </div>
      </div>
    `;
  }

  _renderPageNumbers() {
    const total = this._totalPages;
    const current = this._page;
    const pages = [];

    pages.push(1);
    if (current > 3) pages.push('...');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
      pages.push(i);
    }
    if (current < total - 2) pages.push('...');
    if (total > 1) pages.push(total);

    return pages.map(p =>
      p === '...'
        ? html`<span style="padding: 0 4px; color: var(--color-text-disabled);">...</span>`
        : html`
          <button
            class="activity-page-btn ${p === current ? 'active' : ''}"
            @click=${() => this._goToPage(p)}
          >${p}</button>
        `
    );
  }

  render() {
    if (!this.agent) return html``;

    if (this._loading) {
      return html`
        ${iconStyles}
        ${this._renderSkeleton()}
      `;
    }

    return html`
      ${iconStyles}

      <div class="recent-calls-section">
        <div class="section-header">
          <span class="section-title">Full Activity Log</span>
        </div>

        ${this._events.length === 0 ? html`
          <div class="empty-state" style="padding: var(--space-8);">
            <div class="empty-state-icon"><i class="fa-sharp fa-light fa-timeline"></i></div>
            <div class="empty-state-title">No activity yet</div>
            <div class="empty-state-description">Activity will appear here as this agent handles calls.</div>
          </div>
        ` : html`
          <div class="timeline">
            ${this._events.map(evt => html`
              <div class="timeline-item">
                <div class="timeline-content">
                  <i class="fa-sharp fa-light ${this._eventIcon(evt.eventType)}" style="margin-right:var(--space-1);color:var(--color-text-tertiary);font-size:var(--text-xs);"></i>
                  ${this._eventText(evt)}
                </div>
                <div class="timeline-time">${this._relativeTime(evt.createdAt)}</div>
              </div>
            `)}
          </div>
        `}
      </div>
      ${this._renderPagination()}
    `;
  }
}

customElements.define('agent-activity-tab', AgentActivityTab);
